import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Divider,
  Dialog,
  Button,
} from "@mui/material";
import dayjs from "dayjs";
import logo from "../../../../../assets/SVG/OPDIcon/logo.png";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  printDischargeReport,
  waitForPrintNodeAssets,
} from "../discharge-report/printDischargeReport";
import { useNavigate } from "react-router-dom";
import { log } from "console";
import { set } from "date-fns";

interface Form {
  deathData?: any;
  allData?: any;
  formName?: string;
  id?: string;
  handleClose?: () => void;
  directPrint?: boolean;
  formHeader?: any;
  embedded?: boolean;
}

const DeathReport: React.FC<Form> = ({
  formHeader,
  deathData,
  allData,
  formName,
  id,
  handleClose,
  directPrint,
  embedded = false,
}) => {
  const projectProfile = useSelector(
    (state: any) => state?.projectProfileSlice?.projectProfile
  );
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const loginper = profile?.data?.fullName || profile?.data?.firstName || "";
  const DischargeReportRef = useRef<HTMLDivElement>(null);
  const printedRef = useRef(false);
  const [deathDataa, setDeathDataa] = useState<any>(deathData);
  const [printDataReady, setPrintDataReady] = useState(!directPrint);
  const [headerReady, setHeaderReady] = useState(!directPrint);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [logoLoadingError, setLogoLoadingError] = useState(false);
  const [headerData, setHeaderData] = useState<any>(null);
  const [briefHistory, setBriefHistory] = useState("");
  const [Data, setData] = useState<any>(allData || {});
  // const profile = useSelector((state: any) => state?.profileSlice?.profile);
  // const navigate = useNavigate();
  const handleLogoError = () => {
    setLogoLoadingError(true);
  };

  const pickText = (...values: unknown[]) => {
    for (const value of values) {
      if (value == null || value === "") continue;
      // Saved death forms often default mrn/age to 0 — treat as empty
      if (value === 0 || value === "0") continue;
      const text = String(value).trim();
      if (text) return text;
    }
    return "";
  };

  const formatDate = (...values: unknown[]) => {
    for (const value of values) {
      if (value == null || value === "") continue;
      const parsed = dayjs(value as any);
      if (parsed.isValid()) return parsed.format("DD.MM.YYYY");
    }
    return "N/A";
  };

  const formatTime = (...values: unknown[]) => {
    for (const value of values) {
      if (value == null || value === "") continue;
      const parsed = dayjs(value as any);
      if (parsed.isValid()) return parsed.format("hh:mm A");
    }
    return "N/A";
  };

  const formatDiagnosis = (value: unknown) => {
    if (Array.isArray(value)) {
      const names = value
        .map((item) =>
          typeof item === "string"
            ? item.trim()
            : String(
                (item as any)?.diagnosisName ||
                  (item as any)?.name ||
                  ""
              ).trim()
        )
        .filter(Boolean);
      return names.length ? names.join(", ") : "No diagnosis available";
    }
    if (typeof value === "string" && value.trim()) return value.trim();
    return "No diagnosis available";
  };

  // Death form saves dateTimeDeath / dateTimeDoc; older rows may use split fields
  const deathDateSource =
    deathDataa?.dateTimeDeath ||
    deathDataa?.dateOfDeath ||
    deathDataa?.dateTime ||
    null;
  const docDateSource =
    deathDataa?.dateTimeDoc ||
    deathDataa?.dateOfDoc ||
    deathDataa?.dateTime ||
    null;

  const defaultData = {
    // Prefer patientData (mapped by patientId) over form defaults like mrn: 0
    mrn: pickText(
      deathDataa?.patientData?.mrn,
      deathDataa?.mrn
    ),
    patientName: pickText(
      deathDataa?.patientData?.patientName,
      deathDataa?.patientName
    ),
    guardiansName: pickText(
      deathDataa?.patientData?.guardiansName,
      deathDataa?.guardiansName
    ),
    gender: [
      pickText(deathDataa?.age),
      pickText(
        deathDataa?.patientData?.gender,
        deathDataa?.gender,
        deathDataa?.sex
      ),
    ]
      .filter(Boolean)
      .join(" / "),
    dateOfAdmission: formatDate(
      deathDataa?.dateOfAdmission,
      deathDataa?.admissionDateAndTime
    ),
    dateOfDeath: formatDate(deathDateSource, deathDataa?.dateOfDeath),
    timeOfDeath: formatTime(deathDateSource, deathDataa?.timeOfDeath),
    diagnosis: formatDiagnosis(deathDataa?.diagnosis),
    primaryCauseOfDeath: pickText(deathDataa?.primaryCauseOfDeath),
    secondaryCauseOfDeath: pickText(deathDataa?.secondaryCauseOfDeath),
    doctorName: pickText(deathDataa?.doctorName, deathDataa?.surgeonName),
    currentDate: formatDate(deathDataa?.currentDate, deathDataa?.date, dayjs()),
    dateOfDoc: formatDate(docDateSource, deathDataa?.dateOfDoc),
    timeOfDoc: formatTime(docDateSource, deathDataa?.timeOfDoc),
    deadBodyReceivedBy: pickText(deathDataa?.deadBodyReceivedBy),
    relationWithDiseased: pickText(deathDataa?.relationWithDiseased),
    cnicOfReceiver: pickText(deathDataa?.cnicOfReceiver),
    phoneNumberOfReceiver: pickText(deathDataa?.phoneNumberOfReceiver),
    orderedBy: pickText(deathDataa?.orderedBy),
  };

  const fetchHeaderFormData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/cms/get_prescription_header_and_footer?userId=${id}&location=default`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const fetchedData = response?.data?.data;
      setHeaderData(fetchedData);
    } catch (error) {
      toast.error("Failed to fetch form data.");
    } finally {
      setHeaderReady(true);
    }
  };
  useEffect(() => {
    fetchHeaderFormData();
  }, [id]);

  const fetchAdmissionSnapshot = async () => {
    if (!id) return deathData || null;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_IPD_API_URL}/ipd-admission/getIpdAdmissionById/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response?.data?.data || deathData || null;
    } catch {
      return deathData || null;
    }
  };

  const fetchFormData = async (formName: string) => {
    try {
      // Backend maps patientData by patientId on this endpoint
      const formResponse = await axios.get(
        `${process.env.REACT_APP_IPD_API_URL}/ipd-form-data/getFormDataBySearchId/${id}?formName=${formName}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
          params: {
            _: Date.now(),
          },
        }
      );
      const payload = formResponse?.data?.data || {};
      const fetchedData = payload?.formData || {};
      // Prefer backend-mapped patientData (from patientId); then form; then admission row prop
      const patient = payload?.patientData || deathData?.patientData || {};
      const admission = (await fetchAdmissionSnapshot()) || deathData || {};

      const merged = {
        ...fetchedData,
        // patientData from backend (by patientId) wins over empty/zero form fields
        patientName: pickText(
          patient?.patientName,
          payload?.patientName,
          fetchedData?.patientName,
          admission?.patientName,
          deathData?.patientName
        ),
        mrn: pickText(
          patient?.mrn,
          payload?.mrn,
          admission?.mrn,
          deathData?.mrn,
          deathData?.patientData?.mrn,
          fetchedData?.mrn
        ),
        guardiansName: pickText(
          patient?.guardiansName,
          fetchedData?.guardiansName,
          admission?.guardiansName,
          deathData?.guardiansName
        ),
        gender: pickText(
          patient?.gender,
          fetchedData?.gender,
          admission?.gender,
          deathData?.gender,
          deathData?.patientData?.gender
        ),
        age: pickText(fetchedData?.age, admission?.age),
        dob: patient?.dob || admission?.dob || null,
        dateOfAdmission:
          fetchedData?.dateOfAdmission ||
          payload?.admissionDateAndTime ||
          admission?.admissionDateAndTime ||
          admission?.dateOfAdmission ||
          deathData?.admissionDateAndTime ||
          null,
        admissionDateAndTime:
          payload?.admissionDateAndTime ||
          admission?.admissionDateAndTime ||
          fetchedData?.dateOfAdmission ||
          null,
        doctorName: pickText(
          fetchedData?.doctorName,
          payload?.doctorName,
          admission?.doctorName,
          deathData?.doctorName
        ),
        dateTimeDeath:
          fetchedData?.dateTimeDeath ||
          fetchedData?.dateOfDeath ||
          null,
        dateTimeDoc:
          fetchedData?.dateTimeDoc ||
          fetchedData?.dateOfDoc ||
          null,
        patientData: {
          patientName: pickText(
            patient?.patientName,
            fetchedData?.patientName,
            payload?.patientName,
            admission?.patientName
          ),
          mrn: pickText(patient?.mrn, fetchedData?.mrn, payload?.mrn, admission?.mrn),
          guardiansName: pickText(
            patient?.guardiansName,
            fetchedData?.guardiansName,
            admission?.guardiansName
          ),
          gender: pickText(
            patient?.gender,
            fetchedData?.gender,
            admission?.gender
          ),
          dob: patient?.dob || null,
        },
      };

      setDeathDataa(merged);
      setData({
        ...payload,
        patientData: merged.patientData,
        signature: payload?.signature,
        stamp: payload?.stamp,
      });
      const historyText = await loadBriefHistory(id);
      setBriefHistory(historyText);
    } catch (error) {
      toast.error("Failed to fetch form data.");
    } finally {
      setPrintDataReady(true);
    }
  };

  const loadBriefHistory = async (searchId: any) => {
    const sources = ["DischargeForm", "Patient Refferal Form", "History"];
    for (const source of sources) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_IPD_API_URL}/ipd-form-data/getFormDataBySearchId/${searchId}?formName=${source}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const formData = response?.data?.data?.formData;
        const text = formData?.briefHistory || formData?.detailed_History;
        if (text) return text;
      } catch {
        /* Try the next saved form. */
      }
    }
    return "";
  };

  useEffect(() => {
    if (formName) {
      fetchFormData(formName);
      return;
    }
    if (directPrint) {
      setPrintDataReady(true);
      setHeaderReady(true);
    }
  }, [id, formName]);

  useEffect(() => {
    if (!directPrint || !printDataReady || !headerReady) return;
    if (printedRef.current) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30;
    let retryTimer: number | undefined;

    const waitForPaint = () =>
      new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => resolve());
        });
      });

    const isContentReady = (node: HTMLDivElement | null) => {
      if (!node) return false;
      const text = node.textContent?.replace(/\s+/g, " ").trim() || "";
      const hasTitle = /DEATH CERTIFICATE/i.test(text);
      const hasPatient =
        Boolean(deathDataa?.patientName) ||
        Boolean(deathDataa?.mrn) ||
        Boolean(String(deathDataa?.primaryCauseOfDeath || "").trim());
      // Prefer waiting for fetched form fields; fall back to shell if API returned empty
      return hasTitle && text.length > 80 && (hasPatient || attempts >= 12);
    };

    const tryPrint = async () => {
      if (cancelled || printedRef.current) return;
      const node = DischargeReportRef.current;
      if (!isContentReady(node)) {
        attempts += 1;
        if (attempts < maxAttempts) {
          retryTimer = window.setTimeout(tryPrint, 150);
          return;
        }
        toast.error("Print content is not ready. Please try again.");
        handleClose?.();
        return;
      }

      printedRef.current = true;
      try {
        await waitForPaint();
        if (DischargeReportRef.current) {
          await waitForPrintNodeAssets(DischargeReportRef.current);
        }
        await new Promise((resolve) => window.setTimeout(resolve, 200));
        if (cancelled || !DischargeReportRef.current) return;
        await printDischargeReport(
          DischargeReportRef.current,
          "Death Certificate"
        );
      } catch (error) {
        console.error(error);
        toast.error("Unable to open the print preview.");
      } finally {
        if (!cancelled) handleClose?.();
      }
    };

    retryTimer = window.setTimeout(tryPrint, 150);
    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [
    directPrint,
    printDataReady,
    headerReady,
    deathDataa?.patientName,
    deathDataa?.mrn,
    deathDataa?.primaryCauseOfDeath,
  ]);

  const handleYesPrint = () => {
    // handlePrintData();
    setDialogOpen(false);
  };

  const handleCancelPrint = () => {
    if (handleClose) {
      handleClose();
    }
  };

  return (
    // sx={{ border: "1px solid #ddd" }}
    <Box
      ref={DischargeReportRef}
      className={embedded ? undefined : "discharge-print-root clinical-print"}
      sx={{ color: "#000", background: "#fff" }}
    >
      {!embedded && (
      <div
        className="discharge-letterhead"
        style={{
          width: "100%",
          height: formHeader?.headerUrl || headerData?.headerUrl ? "120px" : "0",
          overflow: "hidden",
        }}
      >
        {(formHeader?.headerUrl || headerData?.headerUrl) && (
          <img
            src={formHeader?.headerUrl || headerData?.headerUrl}
            alt=""
            style={{
              width: "100%",
              height: "120px",
              objectFit: "fill",
              display: "block",
            }}
          />
        )}
      </div>
      )}
      <Box className="death-cert-body force-print-flow" sx={{ padding: "12px 16px 20px" }}>
        {(Data?.patientData || defaultData?.patientName || defaultData?.mrn) && (
          <Box mb={1.5} pb={1} sx={{ borderBottom: "2px solid #000" }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography
                  variant="h6"
                  style={{
                    color: "#000",
                    fontWeight: "bold",
                    fontSize: "13px",
                  }}
                >
                  Patient Name :{" "}
                  <span
                    style={{
                      color: "#000",
                      fontSize: "13px",
                      fontWeight: "normal",
                      textTransform: "capitalize",
                    }}
                  >
                    {Data?.patientData?.patientName || defaultData?.patientName}
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" justifyContent="flex-end">
                  <Typography
                    variant="h6"
                    style={{
                      color: "#000",
                      fontWeight: "bold",
                      fontSize: "13px",
                    }}
                  >
                    MRN # :{" "}
                    <span
                      style={{
                        color: "#000",
                        fontSize: "13px",
                        fontWeight: "normal",
                      }}
                    >
                      {Data?.patientData?.mrn || defaultData?.mrn}
                    </span>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
        <Typography
          variant="h6"
          align="center"
          gutterBottom
          sx={{ fontSize: "18px", color: "#000", fontWeight: 700 }}
        >
          DEATH CERTIFICATE
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={1}>
          {/* Patient Details */}
          <Grid item xs={12}>
            <TableContainer>
              <Table size="small" sx={{ border: "1px solid #ddd" }}>
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      <strong>MR Number</strong>
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      {defaultData?.mrn}
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      <strong>Date</strong>
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      {defaultData?.currentDate}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      <strong>Patient Name</strong>
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      {defaultData?.patientName}
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      <strong>Date of Admission</strong>
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      {defaultData?.dateOfAdmission}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      <strong>S/o, D/o, W/o</strong>
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      {defaultData?.guardiansName}
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      <strong>Age/Sex</strong>
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      {defaultData?.gender}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      <strong>Date of Death</strong>
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      {defaultData?.dateOfDeath}
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      <strong>Time of Death</strong>
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      {defaultData?.timeOfDeath}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Divider sx={{ width: "100%", my: 2 }} />

          {/* Diagnosis */}
          <Grid item xs={12}>
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ fontSize: "11px" }}
            >
              Diagnosis:
            </Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {defaultData?.diagnosis}
            </Typography>
          </Grid>

          <Divider sx={{ width: "100%", my: 1 }} />

          <Grid item xs={12}>
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ fontSize: "11px" }}
            >
              Brief History:
            </Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {briefHistory || "N/A"}
            </Typography>
          </Grid>

          <Divider sx={{ width: "100%", my: 1 }} />

          {/* Cause of Death */}
          <Grid item xs={12}>
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ fontSize: "11px" }}
            >
              Primary Cause of Death:
            </Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {defaultData?.primaryCauseOfDeath}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ fontSize: "11px" }}
            >
              Secondary Cause of Death:
            </Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {defaultData?.secondaryCauseOfDeath}
            </Typography>
          </Grid>

          <Divider sx={{ width: "100%", my: 1 }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ fontSize: "11px" }}
              >
                Dead Body Receiver:
              </Typography>
              <Typography sx={{ fontSize: "11px" }}>
                {defaultData?.deadBodyReceivedBy}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ fontSize: "11px" }}
              >
                Receiver with Relation:
              </Typography>
              <Typography sx={{ fontSize: "11px" }}>
                {defaultData?.relationWithDiseased}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ fontSize: "11px" }}
              >
                Receiver CNIC:
              </Typography>
              <Typography sx={{ fontSize: "11px" }}>
                {defaultData?.cnicOfReceiver}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ fontSize: "11px" }}
              >
                Receiver Phone#:
              </Typography>
              <Typography sx={{ fontSize: "11px" }}>
                {defaultData?.phoneNumberOfReceiver}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ fontSize: "11px" }}
              >
                Ordered By:
              </Typography>
              <Typography sx={{ fontSize: "11px" }}>
                {defaultData?.orderedBy}
              </Typography>
            </Grid>

          </Grid>

          <Divider sx={{ width: "100%", my: 1 }} />

          {/* Doctor Details */}
          <Grid item xs={12}>
            <TableContainer>
              <Table size="small" sx={{ border: "1px solid #ddd" }}>
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      <strong>Doctor Name:</strong> {defaultData?.doctorName}
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      <strong>Date:</strong> {defaultData?.dateOfDoc}
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #ddd", fontSize: "11px" }}
                    >
                      <strong>Time:</strong> {defaultData?.timeOfDoc}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>

      {!embedded && (
      <div className="discharge-page-footer">
        {(Data?.signature || Data?.stamp || formHeader?.signature || formHeader?.stamp) && (
          <div className="discharge-footer-sign">
            {(Data?.signature || (!Data?.stamp && formHeader?.signature)) && (
              <img src={Data?.signature || formHeader?.signature} alt="" />
            )}
            {(Data?.stamp || (!Data?.signature && formHeader?.stamp)) && (
              <img
                src={Data?.stamp || formHeader?.stamp}
                alt=""
                className="discharge-sign-stamp"
              />
            )}
          </div>
        )}
        <div className="discharge-footer-note">
          Electronically verified Report. No Signature Required. Not Valid for any court of law.
        </div>
        {(formHeader?.footerUrl || headerData?.footerUrl) && (
          <img
            className="discharge-footer-image"
            src={formHeader?.footerUrl || headerData?.footerUrl}
            alt=""
            style={{
              width: "100%",
              height: "70px",
              objectFit: "fill",
              display: "block",
            }}
          />
        )}
        <div className="discharge-footer-meta">
          <span>
            Doctor: {defaultData?.doctorName || ""} | {defaultData?.dateOfDoc}{" "}
            {defaultData?.timeOfDoc}
          </span>
          <span>
            Printed By: {loginper} | {dayjs().format("DD.MM.YYYY hh:mm A")}
          </span>
        </div>
      </div>
      )}

    </Box>
  );
};

export default DeathReport;
